import type { QuickPanelDrawerSize } from '@zrsystem/ui';
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

export type QuickPanelParams = Record<string, unknown>;

export interface QuickPanelRegistryEntry {
  title: string;
  dockLabel: string;
  /** Modo B — crumb pai quando painel único (ex.: Clientes › Novo cliente). */
  trailParent?: string;
  size?: QuickPanelDrawerSize;
  shortcut?: 1 | 2 | 3 | 4 | 5;
  component: LazyExoticComponent<ComponentType<QuickPanelParams>>;
}

export type QuickPanelKey = string;

export const quickPanelRegistry: Record<string, QuickPanelRegistryEntry> = {
  'appointment-list': {
    title: 'Agenda',
    dockLabel: 'A',
    shortcut: 1,
    size: 'xl',
    component: lazy(async () => {
      const module = await import('@/features/appointment/surfaces/appointment-week.panel.js');
      return {
        default: module.AppointmentWeekPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'appointment-create': {
    title: 'Novo agendamento',
    trailParent: 'Agenda',
    dockLabel: 'Novo',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/appointment/surfaces/appointment-create.quick-panel.js');
      return {
        default: module.AppointmentCreateQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'place-notification-campaign-form': {
    title: 'Nova campanha',
    trailParent: 'Campanhas',
    dockLabel: 'Campanha',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/notification/surfaces/place-notification-campaign-form.quick-panel.js');
      return {
        default: module.PlaceNotificationCampaignFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-list': {
    title: 'Ordens de serviço',
    dockLabel: 'OS',
    shortcut: 2,
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/components/service-order-list-panel.js');
      return { default: module.ServiceOrderListPanel };
    }),
  },
  'service-order.check-in': {
    title: 'Check-in',
    dockLabel: 'Check-in',
    size: 'xl',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/open-reception.quick-panel.js');
      return {
        default: module.OpenReceptionQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order.quote': {
    title: 'Orçamento',
    dockLabel: 'Orçamento',
    size: 'xl',
    component: lazy(async () => {
      const module = await import('@/features/service-order/surfaces/service-quote.quick-panel.js');
      return {
        default: module.ServiceQuoteQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'useful-life-due': {
    title: 'Vencimentos',
    trailParent: 'Check-in',
    dockLabel: 'Venc.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/useful-life-due.quick-panel.js');
      return {
        default: module.UsefulLifeDueQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-line-search': {
    title: 'Adicionar item',
    dockLabel: 'Catálogo',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/catalog-line-search.quick-panel.js');
      return {
        default: module.CatalogLineSearchQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-quote-add-item': {
    title: 'Adicionar item',
    trailParent: 'Orçamento',
    dockLabel: 'Item',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/service-quote-mutation.quick-panel.js');
      return {
        default: module.ServiceOrderQuoteAddItemQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-quote-apply-revision-plan': {
    title: 'Aplicar plano de revisão',
    trailParent: 'Orçamento',
    dockLabel: 'Plano',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/apply-revision-plan.quick-panel.js');
      return {
        default: module.ApplyRevisionPlanToQuoteQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-apply-revision-plan': {
    title: 'Aplicar plano de revisão',
    trailParent: 'Ordem de serviço',
    dockLabel: 'Plano',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/apply-revision-plan.quick-panel.js');
      return {
        default:
          module.ApplyRevisionPlanToServiceOrderQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-quote-apply-maintenance-checklist': {
    title: 'Aplicar check-list de manutenção',
    trailParent: 'Orçamento',
    dockLabel: 'Check-list',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/apply-maintenance-checklist.quick-panel.js');
      return {
        default:
          module.ApplyMaintenanceChecklistToQuoteQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-apply-maintenance-checklist': {
    title: 'Aplicar check-list de manutenção',
    trailParent: 'Ordem de serviço',
    dockLabel: 'Check-list',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/apply-maintenance-checklist.quick-panel.js');
      return {
        default:
          module.ApplyMaintenanceChecklistToServiceOrderQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-quote-apply-kit-slot': {
    title: 'Aplicar kit SLOT',
    trailParent: 'Orçamento',
    dockLabel: 'Kit',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/apply-kit-slot.quick-panel.js');
      return {
        default: module.ApplyKitSlotToQuoteQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-apply-kit-slot': {
    title: 'Aplicar kit SLOT',
    trailParent: 'Ordem de serviço',
    dockLabel: 'Kit',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/apply-kit-slot.quick-panel.js');
      return {
        default: module.ApplyKitSlotToServiceOrderQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-quote-send': {
    title: 'Enviar orçamento',
    trailParent: 'Orçamento',
    dockLabel: 'Enviar',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/service-quote-mutation.quick-panel.js');
      return {
        default: module.ServiceOrderQuoteSendQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-quote-approve-all': {
    title: 'Registrar aprovação',
    trailParent: 'Orçamento',
    dockLabel: 'Aprovar',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/service-quote-mutation.quick-panel.js');
      return {
        default: module.ServiceOrderQuoteApproveAllQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-quote-reject': {
    title: 'Registrar recusa',
    trailParent: 'Orçamento',
    dockLabel: 'Recusar',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/service-quote-mutation.quick-panel.js');
      return {
        default: module.ServiceOrderQuoteRejectQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-quote-resend': {
    title: 'Reenviar orçamento',
    trailParent: 'Orçamento',
    dockLabel: 'Reenviar',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/service-quote-mutation.quick-panel.js');
      return {
        default: module.ServiceOrderQuoteResendQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-quote-approve-items': {
    title: 'Aprovar itens',
    trailParent: 'Orçamento',
    dockLabel: 'Aprovar',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/service-quote-mutation.quick-panel.js');
      return {
        default: module.ServiceOrderQuoteApproveAllQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-items-list': {
    title: 'Itens da OS',
    trailParent: 'Ordem de serviço',
    dockLabel: 'Itens',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/service-order-items.quick-panel.js');
      return {
        default: module.ServiceOrderItemsListQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-open-quote-purchase-orders': {
    title: 'Itens cotados',
    trailParent: 'Ordem de serviço',
    dockLabel: 'Cotações',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/service-order-open-quote-purchase-orders.quick-panel.js');
      return {
        default:
          module.ServiceOrderOpenQuotePurchaseOrdersQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-add-item': {
    title: 'Adicionar Item',
    trailParent: 'Ordem de serviço',
    dockLabel: 'Item',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/service-order-items.quick-panel.js');
      return {
        default: module.ServiceOrderAddItemQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-item-detail': {
    title: 'Item da OS',
    trailParent: 'Ordem de serviço',
    dockLabel: 'Item',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/service-order-items.quick-panel.js');
      return {
        default: module.ServiceOrderItemDetailQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-item-apply': {
    title: 'Aplicar item',
    trailParent: 'Ordem de serviço',
    dockLabel: 'Aplicar',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/service-order-item-execution.quick-panel.js');
      return {
        default: module.ServiceOrderItemApplyQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-deliver': {
    title: 'Entregar veículo',
    trailParent: 'Ordem de serviço',
    dockLabel: 'Entregar',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/service-order-checkout.quick-panel.js');
      return {
        default: module.ServiceOrderDeliverQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-assign-mechanic': {
    title: 'Adicionar mecânico',
    trailParent: 'Ordem de serviço',
    dockLabel: 'Mecânico',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/service-order-assignment.quick-panel.js');
      return {
        default: module.ServiceOrderAssignMechanicQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'service-order-allocate-workbay': {
    title: 'Alocar box/vaga',
    trailParent: 'Ordem de serviço',
    dockLabel: 'Box',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/service-order/surfaces/service-order-assignment.quick-panel.js');
      return {
        default: module.ServiceOrderAllocateWorkbayQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'place-users-list': {
    title: 'Usuários da unidade',
    dockLabel: 'Usuários',
    size: 'md',
    component: lazy(async () => {
      const module = await import('@/features/identity/surfaces/place-users-list.quick-panel.js');
      return { default: module.PlaceUsersListQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'place-workbays-list': {
    title: 'Boxes da unidade',
    dockLabel: 'Boxes',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/identity/surfaces/place-workbays-list.quick-panel.js');
      return { default: module.PlaceWorkbaysListQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'organization-settings-inventory-policies-form': {
    title: 'Políticas de estoque',
    trailParent: 'Configurações da organização',
    dockLabel: 'Estoque',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/organization-settings/surfaces/inventory-policies-form.quick-panel.js');
      return {
        default: module.InventoryPoliciesFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'organization-settings-commission-policy-form': {
    title: 'Comissão da organização',
    trailParent: 'Configurações da organização',
    dockLabel: 'Comissão',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/organization-settings/surfaces/commission-policy-form.quick-panel.js');
      return {
        default: module.CommissionPolicyFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'identity-place-settings-labor-rate-form': {
    title: 'Taxa de mão de obra',
    trailParent: 'Configurações da unidade',
    dockLabel: 'Taxa',
    size: 'sm',
    component: lazy(async () => {
      const module =
        await import('@/features/identity/surfaces/place-labor-rate-form.quick-panel.js');
      return {
        default: module.PlaceLaborRateFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'identity-place-settings-commission-policy-form': {
    title: 'Comissão da unidade',
    trailParent: 'Configurações da unidade',
    dockLabel: 'Comissão',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/identity/surfaces/place-commission-policy-form.quick-panel.js');
      return {
        default: module.PlaceCommissionPolicyFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'identity-place-settings-address-form': {
    title: 'Endereço operacional',
    trailParent: 'Configurações da unidade',
    dockLabel: 'End.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/identity/surfaces/place-operational-address-form.quick-panel.js');
      return {
        default: module.PlaceOperationalAddressFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'identity-place-settings-agenda-form': {
    title: 'Agenda da unidade',
    trailParent: 'Configurações da unidade',
    dockLabel: 'Agenda',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/identity/surfaces/place-schedule-settings-form.quick-panel.js');
      return {
        default: module.PlaceScheduleSettingsFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'identity-place-settings-sector-form': {
    title: 'Novo setor',
    trailParent: 'Configurações da unidade',
    dockLabel: 'Setor',
    size: 'md',
    component: lazy(async () => {
      const module = await import('@/features/identity/surfaces/place-sector-form.quick-panel.js');
      return { default: module.PlaceSectorFormQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'identity-place-settings-workbay-form': {
    title: 'Novo box',
    trailParent: 'Configurações da unidade',
    dockLabel: 'Box',
    size: 'md',
    component: lazy(async () => {
      const module = await import('@/features/identity/surfaces/place-workbay-form.quick-panel.js');
      return { default: module.PlaceWorkbayFormQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'identity-place-settings-delete': {
    title: 'Excluir',
    trailParent: 'Configurações da unidade',
    dockLabel: 'Excluir',
    size: 'sm',
    component: lazy(async () => {
      const module =
        await import('@/features/identity/surfaces/place-settings-delete.quick-panel.js');
      return {
        default: module.PlaceSettingsDeleteQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'identity-place-settings-storage-form': {
    title: 'Novo armazém',
    trailParent: 'Configurações da unidade',
    dockLabel: 'Armaz.',
    size: 'md',
    component: lazy(async () => {
      const module = await import('@/features/identity/surfaces/place-storage-form.quick-panel.js');
      return { default: module.PlaceStorageFormQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'identity-place-settings-collaborator-form': {
    title: 'Classificação do colaborador',
    trailParent: 'Configurações da unidade',
    dockLabel: 'Colab.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/identity/surfaces/place-collaborator-classification-form.quick-panel.js');
      return {
        default:
          module.PlaceCollaboratorClassificationFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'identity-place-settings-collaborator-commission-form': {
    title: 'Comissão do colaborador',
    trailParent: 'Configurações da unidade',
    dockLabel: 'Comissão',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/identity/surfaces/place-collaborator-commission-rate-form.quick-panel.js');
      return {
        default:
          module.PlaceCollaboratorCommissionRateFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'identity-place-settings-collaborator-work-hours-form': {
    title: 'Jornada do colaborador',
    trailParent: 'Configurações da unidade',
    dockLabel: 'Jornada',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/identity/surfaces/place-collaborator-work-hours-form.quick-panel.js');
      return {
        default: module.PlaceCollaboratorWorkHoursFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'identity-place-settings-collaborator-wage-form': {
    title: 'Salário do colaborador',
    trailParent: 'Configurações da unidade',
    dockLabel: 'Salário',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/identity/surfaces/place-collaborator-wage-form.quick-panel.js');
      return {
        default: module.PlaceCollaboratorWageFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'customer-list': {
    title: 'Clientes',
    dockLabel: 'C',
    shortcut: 3,
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/customer/surfaces/customers-list.panel.js');
      return { default: module.CustomersListPanel };
    }),
  },
  'suppliers-list': {
    title: 'Fornecedores',
    dockLabel: 'Forn.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/supplier/surfaces/suppliers-list.quick-panel.js');
      return { default: module.SuppliersListQuickPanel };
    }),
  },
  'add-supplier-role': {
    title: 'Adicionar fornecedor',
    dockLabel: 'Novo',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/supplier/surfaces/add-supplier-role.quick-panel.js');
      return { default: module.AddSupplierRoleQuickPanel };
    }),
  },
  'suppliers-register': {
    title: 'Novo fornecedor',
    trailParent: 'Fornecedores',
    dockLabel: 'Novo',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/supplier/surfaces/suppliers-register.quick-panel.js');
      return { default: module.SuppliersRegisterQuickPanel };
    }),
  },
  'supplier-detail': {
    title: 'Ficha do fornecedor',
    dockLabel: 'Forn.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/supplier/surfaces/supplier-detail.quick-panel.js');
      return { default: module.SupplierDetailQuickPanel };
    }),
  },
  'vehicle-list': {
    title: 'Veículos',
    dockLabel: 'V',
    shortcut: 4,
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/vehicle/components/vehicle-list-panel.js');
      return { default: module.VehicleListPanel };
    }),
  },
  'party-vehicles-list': {
    title: 'Veículos',
    dockLabel: 'Veíc.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/vehicle/surfaces/party-vehicles-list.panel.js');
      return { default: module.PartyVehiclesListPanel };
    }),
  },
  'place-vehicles-list': {
    title: 'Veículos',
    dockLabel: 'Veíc.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/vehicle/surfaces/place-vehicles-list.panel.js');
      return { default: module.PlaceVehiclesListPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'register-vehicle': {
    title: 'Novo veículo',
    trailParent: 'Veículos',
    dockLabel: 'Novo',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/vehicle/surfaces/register-vehicle.panel.js');
      return { default: module.RegisterVehiclePanel as ComponentType<QuickPanelParams> };
    }),
  },
  'vehicle-brands-list': {
    title: 'Marcas',
    trailParent: 'Veículos',
    dockLabel: 'Marca',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/vehicle/surfaces/vehicle-brands-list.panel.js');
      return { default: module.VehicleBrandsListPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'vehicle-models-list': {
    title: 'Modelos',
    trailParent: 'Veículos',
    dockLabel: 'Modelo',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/vehicle/surfaces/vehicle-models-list.panel.js');
      return { default: module.VehicleModelsListPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'vehicle-catalog-item-form': {
    title: 'Catálogo',
    trailParent: 'Veículos',
    dockLabel: 'Cat.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/vehicle/surfaces/vehicle-catalog-item-form.panel.js');
      return { default: module.VehicleCatalogItemFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'edit-vehicle-profile': {
    title: 'Editar perfil do veículo',
    trailParent: 'Veículos',
    dockLabel: 'Veíc.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/vehicle/surfaces/edit-vehicle-profile.panel.js');
      return {
        default: module.EditVehicleProfilePanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'vehicle-detail': {
    title: 'Veículo',
    dockLabel: 'Veíc.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/vehicle/surfaces/vehicle-detail.panel.js');
      return { default: module.VehicleDetailPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'record-mileage': {
    title: 'Registrar quilometragem',
    trailParent: 'Veículos',
    dockLabel: 'KM',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/vehicle/surfaces/record-mileage.panel.js');
      return { default: module.RecordMileagePanel as ComponentType<QuickPanelParams> };
    }),
  },
  'record-vehicle-maintenance': {
    title: 'Registrar manutenção',
    trailParent: 'Veículos',
    dockLabel: 'Manut.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/vehicle/surfaces/record-vehicle-maintenance.panel.js');
      return {
        default: module.RecordVehicleMaintenancePanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'vehicle-part-form': {
    title: 'Peça do veículo',
    trailParent: 'Veículos',
    dockLabel: 'Peça',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/vehicle/surfaces/vehicle-part-form.panel.js');
      return { default: module.VehiclePartFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-services-list': {
    title: 'Buscar serviço',
    dockLabel: 'Srv.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/services-list.panel.js');
      return { default: module.ServicesListPanel };
    }),
  },
  'catalog-products-list': {
    title: 'Buscar produto',
    dockLabel: 'Prod.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/products-list.panel.js');
      return { default: module.ProductsListPanel };
    }),
  },
  'catalog-products-applicable': {
    title: 'Produtos aplicáveis ao veículo',
    dockLabel: 'Apl.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/applicable-products-list.panel.js');
      return { default: module.ApplicableProductsListPanel };
    }),
  },
  'catalog-services-applicable': {
    title: 'Serviços aplicáveis ao veículo',
    dockLabel: 'Apl.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/applicable-services-list.panel.js');
      return { default: module.ApplicableServicesListPanel };
    }),
  },
  'catalog-taxonomy-list': {
    title: 'Taxonomias',
    dockLabel: 'Tax.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/taxonomy-list.panel.js');
      return { default: module.TaxonomyListPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-taxonomy-form': {
    title: 'Taxonomia',
    dockLabel: 'Form',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/taxonomy-form.panel.js');
      return { default: module.TaxonomyFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'register-service': {
    title: 'Novo serviço',
    trailParent: 'Serviços',
    dockLabel: 'Novo',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/register-service.panel.js');
      return { default: module.RegisterServicePanel as ComponentType<QuickPanelParams> };
    }),
  },
  'register-product': {
    title: 'Novo produto',
    trailParent: 'Produtos',
    dockLabel: 'Novo',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/register-product.panel.js');
      return { default: module.RegisterProductPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'change-service-status': {
    title: 'Alterar status',
    dockLabel: 'Status',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/change-service-status.panel.js');
      return {
        default: module.ChangeServiceStatusPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'change-product-status': {
    title: 'Alterar status',
    dockLabel: 'Status',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/change-product-status.panel.js');
      return {
        default: module.ChangeProductStatusPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'edit-product': {
    title: 'Editar produto',
    trailParent: 'Produtos',
    dockLabel: 'Prod.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/edit-product.panel.js');
      return { default: module.EditProductPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-product-tax-profile-form': {
    title: 'Perfil fiscal do produto',
    trailParent: 'Produto',
    dockLabel: 'Fiscal',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/product-tax-profile-form.panel.js');
      return { default: module.ProductTaxProfileFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-service-tax-profile-form': {
    title: 'Perfil fiscal do serviço',
    trailParent: 'Serviço',
    dockLabel: 'Fiscal',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/service-tax-profile-form.panel.js');
      return { default: module.ServiceTaxProfileFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-product-fitment-form': {
    title: 'Aplicação veicular',
    trailParent: 'Produto',
    dockLabel: 'Aplic.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/product-fitment-form.panel.js');
      return { default: module.ProductFitmentFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-service-fitment-form': {
    title: 'Aplicação veicular',
    trailParent: 'Serviço',
    dockLabel: 'Aplic.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/service-fitment-form.panel.js');
      return { default: module.ServiceFitmentFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'edit-service': {
    title: 'Editar serviço',
    trailParent: 'Serviços',
    dockLabel: 'Srv.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/edit-service.panel.js');
      return { default: module.EditServicePanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-kits-list': {
    title: 'Buscar kit',
    dockLabel: 'Kit',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/kits-list.panel.js');
      return { default: module.KitsListPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'register-kit': {
    title: 'Novo kit',
    trailParent: 'Kits',
    dockLabel: 'Novo',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/register-kit.panel.js');
      return { default: module.RegisterKitPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'kit-edit': {
    title: 'Editar identificação do kit',
    trailParent: 'Kit',
    dockLabel: 'Kit',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/edit-kit.panel.js');
      return { default: module.KitEditPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'kit-composition-revise': {
    title: 'Revisar composição',
    trailParent: 'Kit',
    dockLabel: 'Comp.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/revise-kit-composition.panel.js');
      return {
        default: module.KitCompositionRevisePanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'kit-components-picker': {
    title: 'Adicionar componentes',
    trailParent: 'Kit',
    dockLabel: 'Comp.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/kit-components-picker.panel.js');
      return { default: module.KitComponentsPickerPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'change-kit-status': {
    title: 'Alterar status',
    dockLabel: 'Status',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/change-kit-status.panel.js');
      return { default: module.ChangeKitStatusPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-revision-plans-list': {
    title: 'Buscar plano de revisão',
    dockLabel: 'Plano',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/revision-plans-list.panel.js');
      return { default: module.RevisionPlansListPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'register-revision-plan': {
    title: 'Novo plano de revisão',
    trailParent: 'Planos de revisão',
    dockLabel: 'Novo',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/register-revision-plan.panel.js');
      return { default: module.RegisterRevisionPlanPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'edit-revision-plan': {
    title: 'Editar plano de revisão',
    trailParent: 'Planos de revisão',
    dockLabel: 'Plano',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/edit-revision-plan.panel.js');
      return { default: module.EditRevisionPlanPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-maintenance-checklists-list': {
    title: 'Buscar check-list de manutenção',
    dockLabel: 'Check-list',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/catalog/surfaces/maintenance-checklists-list.panel.js');
      return {
        default: module.MaintenanceChecklistsListPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'register-maintenance-checklist': {
    title: 'Novo check-list de manutenção',
    trailParent: 'Check-lists de manutenção',
    dockLabel: 'Novo',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/catalog/surfaces/register-maintenance-checklist.panel.js');
      return {
        default: module.RegisterMaintenanceChecklistPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'edit-maintenance-checklist': {
    title: 'Editar check-list de manutenção',
    trailParent: 'Check-lists de manutenção',
    dockLabel: 'Check-list',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/catalog/surfaces/edit-maintenance-checklist.panel.js');
      return {
        default: module.EditMaintenanceChecklistPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-price-promotions-form': {
    title: 'Promoção de preço',
    trailParent: 'Promoções',
    dockLabel: 'Promoção',
    size: 'md',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/price-promotion-form.panel.js');
      return { default: module.PricePromotionFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-price-exception-form': {
    title: 'Exceção de preço',
    trailParent: 'Simulador',
    dockLabel: 'Exceção',
    size: 'md',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/price-exception-form.panel.js');
      return { default: module.PriceExceptionFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-price-policy-form': {
    title: 'Cadastrar política',
    trailParent: 'Precificação',
    dockLabel: 'Política',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/catalog/surfaces/default-price-policy-form.panel.js');
      return { default: module.DefaultPricePolicyFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'party-address-form': {
    title: 'Endereço',
    dockLabel: 'End.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/customer/surfaces/party-address-form.panel.js');
      return { default: module.PartyAddressFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'party-contact-form': {
    title: 'Contato',
    dockLabel: 'Cont.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/customer/surfaces/party-contact-form.panel.js');
      return { default: module.PartyContactFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'party-responsible-form': {
    title: 'Responsável',
    dockLabel: 'Resp.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/customer/surfaces/party-responsible-form.panel.js');
      return { default: module.PartyResponsibleFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'customer-alert-cadence': {
    title: 'Recorrência dos avisos',
    dockLabel: 'Rec.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/customer/surfaces/customer-alert-cadence.panel.js');
      return { default: module.CustomerAlertCadencePanel as ComponentType<QuickPanelParams> };
    }),
  },
  'marketing-consent-revoke': {
    title: 'Revogar consentimento',
    dockLabel: 'Rev.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/customer/surfaces/marketing-consent-revoke.panel.js');
      return { default: module.MarketingConsentRevokePanel as ComponentType<QuickPanelParams> };
    }),
  },
  'party-change-log-detail': {
    title: 'Detalhe da alteração',
    dockLabel: 'Alt.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/customer/surfaces/party-change-log-detail.panel.js');
      return { default: module.PartyChangeLogDetailPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'customer-role-status-detail': {
    title: 'Detalhe do status',
    dockLabel: 'St.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/customer/surfaces/customer-role-status-detail.panel.js');
      return { default: module.CustomerRoleStatusDetailPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'supplier-capability-form': {
    title: 'Serviço',
    dockLabel: 'Srv.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/supplier/surfaces/supplier-capability-form.panel.js');
      return { default: module.SupplierCapabilityFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'supplier-product-form': {
    title: 'Vínculo de produto',
    dockLabel: 'Prod.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/supplier/surfaces/supplier-product-form.panel.js');
      return { default: module.SupplierProductFormPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'customers-register': {
    title: 'Novo cliente',
    trailParent: 'Clientes',
    dockLabel: 'Cad.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/customer/surfaces/customers-register.quick-panel.js');
      return { default: module.CustomersRegisterQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'register-party': {
    title: 'Novo cliente',
    trailParent: 'Clientes',
    dockLabel: 'Cad.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/customer/surfaces/customers-register.quick-panel.js');
      return { default: module.CustomersRegisterQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'customers-edit': {
    title: 'Editar cliente',
    trailParent: 'Clientes',
    dockLabel: 'Edit.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/customer/surfaces/customers-edit.quick-panel.js');
      return { default: module.CustomersEditQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'organizations-provision': {
    title: 'Nova organização',
    trailParent: 'Organizações',
    dockLabel: 'Nova',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/organizations-provision.quick-panel.js');
      return {
        default: module.OrganizationsProvisionQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'edit-organization': {
    title: 'Editar organização',
    trailParent: 'Organização',
    dockLabel: 'Edit.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/platform/surfaces/organizations-edit.quick-panel.js');
      return { default: module.OrganizationsEditQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'organization-join-network': {
    title: 'Vincular à rede',
    trailParent: 'Organização',
    dockLabel: 'Rede',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/organization-join-network.quick-panel.js');
      return {
        default: module.OrganizationJoinNetworkQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'networks-register': {
    title: 'Nova rede',
    trailParent: 'Redes',
    dockLabel: 'Nova',
    size: 'md',
    component: lazy(async () => {
      const module = await import('@/features/platform/surfaces/networks-register.quick-panel.js');
      return { default: module.NetworksRegisterQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'networks-edit': {
    title: 'Editar rede',
    trailParent: 'Rede',
    dockLabel: 'Edit.',
    size: 'md',
    component: lazy(async () => {
      const module = await import('@/features/platform/surfaces/networks-edit.quick-panel.js');
      return { default: module.NetworksEditQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-taxonomy-register': {
    title: 'Novo nó',
    trailParent: 'Taxonomia',
    dockLabel: 'Novo',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-taxonomy-register.quick-panel.js');
      return {
        default: module.CatalogTaxonomyRegisterQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-taxonomy-edit': {
    title: 'Editar nó',
    trailParent: 'Taxonomia',
    dockLabel: 'Edit.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-taxonomy-edit.quick-panel.js');
      return { default: module.CatalogTaxonomyEditQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-taxonomy-move': {
    title: 'Mover nó',
    trailParent: 'Taxonomia',
    dockLabel: 'Mover',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-taxonomy-move.quick-panel.js');
      return { default: module.CatalogTaxonomyMoveQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-taxonomy-archive': {
    title: 'Arquivar nó',
    trailParent: 'Taxonomia',
    dockLabel: 'Arq.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-taxonomy-archive.quick-panel.js');
      return {
        default: module.CatalogTaxonomyArchiveQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-generic-service-register': {
    title: 'Novo serviço genérico',
    trailParent: 'Serviços genéricos',
    dockLabel: 'Novo',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-generic-service-register.quick-panel.js');
      return {
        default: module.CatalogGenericServiceRegisterQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-generic-service-edit': {
    title: 'Editar serviço genérico',
    trailParent: 'Serviços genéricos',
    dockLabel: 'Edit.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-generic-service-edit.quick-panel.js');
      return {
        default: module.CatalogGenericServiceEditQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-generic-service-archive': {
    title: 'Arquivar serviço genérico',
    trailParent: 'Serviços genéricos',
    dockLabel: 'Arq.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-generic-service-archive.quick-panel.js');
      return {
        default: module.CatalogGenericServiceArchiveQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-generic-register': {
    title: 'Novo genérico',
    trailParent: 'Genéricos',
    dockLabel: 'Novo',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-generic-register.quick-panel.js');
      return {
        default: module.CatalogGenericRegisterQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-generic-edit': {
    title: 'Editar genérico',
    trailParent: 'Genéricos',
    dockLabel: 'Edit.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-generic-edit.quick-panel.js');
      return { default: module.CatalogGenericEditQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'catalog-product-link-generic': {
    title: 'Vincular genérico',
    trailParent: 'Curadoria',
    dockLabel: 'Vínc.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-product-link-generic.quick-panel.js');
      return {
        default: module.CatalogProductLinkGenericQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-product-attributes': {
    title: 'Atributos da peça',
    trailParent: 'Curadoria',
    dockLabel: 'Atr.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-product-attributes.quick-panel.js');
      return {
        default: module.CatalogProductAttributesQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-product-direct-register': {
    title: 'Nova peça',
    trailParent: 'Base global',
    dockLabel: 'Novo',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-product-direct-register.quick-panel.js');
      return {
        default: module.CatalogProductDirectRegisterQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-product-direct-edit': {
    title: 'Editar peça',
    trailParent: 'Base global',
    dockLabel: 'Edit.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-product-direct-edit.quick-panel.js');
      return {
        default: module.CatalogProductDirectEditQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-manufacturer-register': {
    title: 'Novo fabricante',
    trailParent: 'Fabricantes',
    dockLabel: 'Novo',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-manufacturer-register.quick-panel.js');
      return {
        default: module.CatalogManufacturerRegisterQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-manufacturer-edit': {
    title: 'Editar fabricante',
    trailParent: 'Fabricantes',
    dockLabel: 'Edit.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-manufacturer-edit.quick-panel.js');
      return {
        default: module.CatalogManufacturerEditQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-manufacturer-archive': {
    title: 'Arquivar fabricante',
    trailParent: 'Fabricantes',
    dockLabel: 'Arq.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-manufacturer-archive.quick-panel.js');
      return {
        default: module.CatalogManufacturerArchiveQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-generic-archive': {
    title: 'Arquivar genérico',
    trailParent: 'Genéricos',
    dockLabel: 'Arq.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-generic-archive.quick-panel.js');
      return {
        default: module.CatalogGenericArchiveQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-product-applicability-register': {
    title: 'Nova aplicação',
    trailParent: 'Base global',
    dockLabel: 'Apl.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-product-vehicle-application-register.quick-panel.js');
      return {
        default:
          module.CatalogProductVehicleApplicationRegisterQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-product-applicability-edit': {
    title: 'Editar aplicação',
    trailParent: 'Base global',
    dockLabel: 'Apl.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-product-vehicle-application-edit.quick-panel.js');
      return {
        default:
          module.CatalogProductVehicleApplicationEditQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'catalog-product-applicability-archive': {
    title: 'Remover aplicação',
    trailParent: 'Base global',
    dockLabel: 'Rem.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/catalog-product-vehicle-application-archive.quick-panel.js');
      return {
        default:
          module.CatalogProductVehicleApplicationArchiveQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'profiles-register': {
    title: 'Novo perfil',
    trailParent: 'Perfis',
    dockLabel: 'Novo',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/register-profile-template.quick-panel.js');
      return {
        default: module.RegisterProfileTemplateQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'profiles-edit': {
    title: 'Editar perfil',
    trailParent: 'Perfil',
    dockLabel: 'Edit.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/edit-profile-template.quick-panel.js');
      return {
        default: module.EditProfileTemplateQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'organization-resend-bootstrap': {
    title: 'Reenviar convite',
    trailParent: 'Organização',
    dockLabel: 'Convite',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/organization-resend-bootstrap.quick-panel.js');
      return {
        default: module.OrganizationResendBootstrapQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'organization-access-invite': {
    title: 'Novo usuário',
    trailParent: 'Usuários',
    dockLabel: 'Novo',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/organization-access-invite.quick-panel.js');
      return {
        default: module.OrganizationAccessInviteQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'organization-user-invite': {
    title: 'Novo usuário',
    trailParent: 'Usuários',
    dockLabel: 'Novo',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/platform/surfaces/organization-user-invite.quick-panel.js');
      return {
        default: module.OrganizationUserInviteQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'places-provision': {
    title: 'Nova oficina',
    trailParent: 'Oficinas',
    dockLabel: 'Nova',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/platform/surfaces/places-provision.quick-panel.js');
      return { default: module.PlacesProvisionQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'places-edit': {
    title: 'Editar oficina',
    trailParent: 'Oficina',
    dockLabel: 'Edit.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/platform/surfaces/places-edit.quick-panel.js');
      return { default: module.PlacesEditQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'edit-supplier-commercial': {
    title: 'Editar perfil comercial',
    trailParent: 'Fornecedores',
    dockLabel: 'Comercial',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/supplier/surfaces/edit-supplier-commercial.panel.js');
      return { default: module.EditSupplierCommercialPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'suppliers-edit': {
    title: 'Editar fornecedor',
    trailParent: 'Fornecedores',
    dockLabel: 'Edit.',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/supplier/surfaces/suppliers-edit.quick-panel.js');
      return { default: module.SuppliersEditQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'stock-balance-detail': {
    title: 'Detalhe do estoque',
    trailParent: 'Saldo',
    dockLabel: 'Saldo',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/inventory/surfaces/stock-balance-detail.quick-panel.js');
      return { default: module.StockBalanceDetailQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'stock-movement-detail': {
    title: 'Detalhe da movimentação',
    trailParent: 'Movimentações',
    dockLabel: 'Mov.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/inventory/surfaces/stock-movement-detail.quick-panel.js');
      return { default: module.StockMovementDetailQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'stock-transfer-detail': {
    title: 'Detalhe da transferência',
    trailParent: 'Transferências',
    dockLabel: 'Trf.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/inventory/surfaces/stock-transfer-detail.quick-panel.js');
      return { default: module.StockTransferDetailQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'create-stock-transfer': {
    title: 'Nova transferência',
    trailParent: 'Transferências',
    dockLabel: 'Nova',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/inventory/surfaces/create-stock-transfer.quick-panel.js');
      return { default: module.CreateStockTransferQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'receive-stock-transfer': {
    title: 'Receber transferência',
    trailParent: 'Transferências',
    dockLabel: 'Receber',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/inventory/surfaces/receive-stock-transfer.quick-panel.js');
      return { default: module.ReceiveStockTransferQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'receive-purchase-order': {
    title: 'Receber pedido',
    trailParent: 'Pedidos',
    dockLabel: 'Receber',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/purchasing/surfaces/receive-purchase-order.quick-panel.js');
      return { default: module.ReceivePurchaseOrderQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'open-purchase-quote': {
    title: 'Nova cotação',
    trailParent: 'Cotações',
    dockLabel: 'Nova',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/purchasing/surfaces/open-purchase-quote.quick-panel.js');
      return { default: module.OpenPurchaseQuoteQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'add-purchase-quote-item': {
    title: 'Adicionar item',
    trailParent: 'Cotação',
    dockLabel: 'Item',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/purchasing/surfaces/add-purchase-quote-item.quick-panel.js');
      return { default: module.AddPurchaseQuoteItemQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'import-service-quote-into-purchase-quote': {
    title: 'Importar do orçamento',
    trailParent: 'Cotação',
    dockLabel: 'Importar',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/purchasing/surfaces/import-service-quote-into-purchase-quote.quick-panel.js');
      return {
        default:
          module.ImportServiceQuoteIntoPurchaseQuoteQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'invite-supplier-to-quote': {
    title: 'Convidar fornecedores',
    trailParent: 'Cotação',
    dockLabel: 'Forn.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/purchasing/surfaces/invite-supplier-to-quote.quick-panel.js');
      return {
        default: module.InviteSupplierToQuoteQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'record-purchase-quote-response': {
    title: 'Registrar resposta',
    trailParent: 'Cotação',
    dockLabel: 'Resposta',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/purchasing/surfaces/record-purchase-quote-response.quick-panel.js');
      return {
        default: module.RecordPurchaseQuoteResponseQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'create-stock-inventory': {
    title: 'Novo inventário',
    trailParent: 'Inventário físico',
    dockLabel: 'Novo',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/inventory/surfaces/create-stock-inventory.quick-panel.js');
      return { default: module.CreateStockInventoryQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'stock-inventory-detail': {
    title: 'Inventário físico',
    trailParent: 'Inventário físico',
    dockLabel: 'Inv.',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/inventory/surfaces/stock-inventory-detail.quick-panel.js');
      return { default: module.StockInventoryDetailQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'inventory.inbound-conference.line': {
    title: 'Conferir linha',
    trailParent: 'Conferência de entrada',
    dockLabel: 'Linha',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/inventory/surfaces/inbound-conference-line.quick-panel.js');
      return {
        default: module.InboundConferenceLineQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'inventory-nf-pending': {
    title: 'Pendências de NF',
    trailParent: 'Saldo',
    dockLabel: 'NF',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/inventory/surfaces/inventory-nf-pending.quick-panel.js');
      return { default: module.InventoryNfPendingQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'quick-physical-receipt': {
    title: 'Receber peça',
    trailParent: 'Saldo',
    dockLabel: 'Receber',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/inventory/surfaces/quick-physical-receipt.quick-panel.js');
      return { default: module.QuickPhysicalReceiptQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'attach-invoice-to-lot': {
    title: 'Anexar NF',
    trailParent: 'Pendências de NF',
    dockLabel: 'NF',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/inventory/surfaces/attach-invoice-to-lot.quick-panel.js');
      return { default: module.AttachInvoiceToLotQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'reconcile-deferred-consumption': {
    title: 'Reconciliar consumo',
    trailParent: 'Pendências de NF',
    dockLabel: 'Reconc.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/inventory/surfaces/reconcile-deferred-consumption.quick-panel.js');
      return {
        default: module.ReconcileDeferredConsumptionQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'stock-balances-list': {
    title: 'Saldo de estoque',
    dockLabel: 'Saldo',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/inventory/surfaces/stock-balances-list.quick-panel.js');
      return { default: module.StockBalancesListQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'fiscal-ncm-form': {
    title: 'NCM',
    trailParent: 'NCMs',
    dockLabel: 'NCM',
    size: 'md',
    component: lazy(async () => {
      const module = await import('@/features/fiscal/surfaces/fiscal-ncm-form.quick-panel.js');
      return { default: module.FiscalNcmFormQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'identity-geo-city-form': {
    title: 'Cidade',
    trailParent: 'Cidades',
    dockLabel: 'Cidade',
    size: 'md',
    component: lazy(async () => {
      const module = await import('@/features/identity/surfaces/geo-city-form.quick-panel.js');
      return { default: module.GeoCityFormQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'fiscal-inbound-register': {
    title: 'Dar entrada com XML',
    trailParent: 'Documentos fiscais',
    dockLabel: 'Entrada',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/fiscal-inbound-register.quick-panel.js');
      return {
        default: module.FiscalInboundRegisterQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-inbound-xml-preview': {
    title: 'Nota completa (XML)',
    trailParent: 'Dar entrada com XML',
    dockLabel: 'NF-e',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/fiscal-inbound-xml-preview.quick-panel.js');
      return {
        default: module.FiscalInboundXmlPreviewQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-place-tax-profile-form': {
    title: 'Perfil tributário',
    trailParent: 'Parametrização fiscal',
    dockLabel: 'Perfil',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/place-tax-profile-form.quick-panel.js');
      return { default: module.PlaceTaxProfileFormQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'fiscal-place-certificate-form': {
    title: 'Certificado digital',
    trailParent: 'Parametrização fiscal',
    dockLabel: 'Cert.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/place-certificate-form.quick-panel.js');
      return { default: module.PlaceCertificateFormQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'fiscal-place-nfce-csc-form': {
    title: 'CSC NFC-e',
    trailParent: 'Parametrização fiscal',
    dockLabel: 'CSC',
    size: 'sm',
    component: lazy(async () => {
      const module = await import('@/features/fiscal/surfaces/place-nfce-csc-form.quick-panel.js');
      return { default: module.PlaceNfceCscFormQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'fiscal-document-danfe': {
    title: 'DANFE',
    trailParent: 'Documento fiscal',
    dockLabel: 'DANFE',
    size: 'xl',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/fiscal-document-danfe.quick-panel.js');
      return {
        default: module.FiscalDocumentDanfeQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-document-xml': {
    title: 'XML autorizado',
    trailParent: 'Documento fiscal',
    dockLabel: 'XML',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/fiscal/surfaces/fiscal-document-xml.quick-panel.js');
      return {
        default: module.FiscalDocumentXmlQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-document-cancel': {
    title: 'Cancelar NF',
    trailParent: 'Documento fiscal',
    dockLabel: 'Cancel.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/fiscal-document-cancel.quick-panel.js');
      return {
        default: module.FiscalDocumentCancelQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-inbound-cancel': {
    title: 'Cancelar entrada',
    trailParent: 'Documento fiscal',
    dockLabel: 'Cancel.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/fiscal-inbound-cancel.quick-panel.js');
      return {
        default: module.FiscalInboundCancelQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-dfe-inbox-manifestation': {
    title: 'Manifestar',
    trailParent: 'Caixa de entrada',
    dockLabel: 'MDe',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/fiscal-dfe-inbox-manifestation.quick-panel.js');
      return {
        default: module.FiscalDfeInboxManifestationQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-place-emission-settings': {
    title: 'Configurações de emissão',
    trailParent: 'Parametrização fiscal',
    dockLabel: 'Emissão',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/place-emission-settings.quick-panel.js');
      return {
        default: module.PlaceEmissionSettingsQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-place-operation-form': {
    title: 'Operação fiscal',
    trailParent: 'Parametrização fiscal',
    dockLabel: 'Op.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/place-fiscal-operation-form.quick-panel.js');
      return {
        default: module.PlaceFiscalOperationFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-place-rule-set-form': {
    title: 'Motor de regras',
    trailParent: 'Parametrização fiscal',
    dockLabel: 'Regras',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/place-fiscal-operation-form.quick-panel.js');
      return { default: module.PlaceRuleSetFormQuickPanel as ComponentType<QuickPanelParams> };
    }),
  },
  'fiscal-place-tax-profile-detail': {
    title: 'Perfil tributário',
    trailParent: 'Parametrização fiscal',
    dockLabel: 'Perfil',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/fiscal-parametrizacao-detail.quick-panel.js');
      return {
        default: module.PlaceTaxProfileDetailQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-place-certificate-detail': {
    title: 'Certificado digital',
    trailParent: 'Parametrização fiscal',
    dockLabel: 'Cert.',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/fiscal-parametrizacao-detail.quick-panel.js');
      return {
        default: module.PlaceCertificateDetailQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-tenant-enabled-operation-detail': {
    title: 'Operação habilitada',
    trailParent: 'Parametrização fiscal',
    dockLabel: 'Op. tenant',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/fiscal-parametrizacao-detail.quick-panel.js');
      return {
        default: module.TenantEnabledOperationDetailQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-tenant-enabled-operation-form': {
    title: 'Operação habilitada',
    trailParent: 'Parametrização fiscal',
    dockLabel: 'Op. tenant',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/tenant-enabled-operation-form.quick-panel.js');
      return {
        default: module.TenantEnabledOperationFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-place-rule-set-detail': {
    title: 'Motor de regras',
    trailParent: 'Parametrização fiscal',
    dockLabel: 'Regras',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/fiscal-parametrizacao-detail.quick-panel.js');
      return {
        default: module.PlaceTaxRuleSetDetailQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-place-rule-set-editor': {
    title: 'Editor do motor',
    trailParent: 'Parametrização fiscal',
    dockLabel: 'Editor',
    size: 'xl',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/place-rule-set-editor.quick-panel.js');
      return {
        default: module.PlaceRuleSetEditorQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-place-tax-rule-form': {
    title: 'Regra tributária',
    trailParent: 'Motor de regras',
    dockLabel: 'Regra',
    size: 'xl',
    component: lazy(async () => {
      const module = await import('@/features/fiscal/surfaces/place-tax-rule-form.quick-panel.js');
      return {
        default: module.PlaceTaxRuleFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'fiscal-catalog-code-picker': {
    title: 'Catálogo fiscal',
    trailParent: 'Regra tributária',
    dockLabel: 'Catálogo',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/fiscal/surfaces/fiscal-catalog-code-picker.quick-panel.js');
      return {
        default: module.FiscalCatalogCodePickerQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'inbound-charge-policy-form': {
    title: 'Editar política de encargo',
    trailParent: 'Encargos de entrada',
    dockLabel: 'Encargo',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/inbound-charge-policy-form.quick-panel.js');
      return {
        default: module.InboundChargePolicyFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'chart-of-account-form': {
    title: 'Nova conta',
    trailParent: 'Plano de contas',
    dockLabel: 'Conta',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/chart-of-account-form.quick-panel.js');
      return {
        default: module.ChartOfAccountFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'accounts-payable-detail': {
    title: 'Título a pagar',
    trailParent: 'Contas a pagar',
    dockLabel: 'Pagar',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/accounts-payable-detail.quick-panel.js');
      return {
        default: module.AccountsPayableDetailQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'recurring-payable-detail': {
    title: 'Conta fixa',
    trailParent: 'Contas a pagar',
    dockLabel: 'Pagar',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/recurring-payable-detail.quick-panel.js');
      return {
        default: module.RecurringPayableDetailQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'accounts-payable-create': {
    title: 'Novo título a pagar',
    trailParent: 'Contas a pagar',
    dockLabel: 'Pagar',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/accounts-payable-create.quick-panel.js');
      return {
        default: module.AccountsPayableCreateQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'accounts-receivable-create': {
    title: 'Novo título a receber',
    trailParent: 'Contas a receber',
    dockLabel: 'Receber',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/accounts-receivable-create.quick-panel.js');
      return {
        default: module.AccountsReceivableCreateQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'accounts-receivable-detail': {
    title: 'Título a receber',
    trailParent: 'Contas a receber',
    dockLabel: 'Receber',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/accounts-receivable-detail.quick-panel.js');
      return {
        default: module.AccountsReceivableDetailQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'receivable-advance-form': {
    title: 'Antecipar título',
    trailParent: 'Contas a receber',
    dockLabel: 'Antecipar',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/receivable-advance-form.quick-panel.js');
      return {
        default: module.ReceivableAdvanceFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'payment-settings-policy-form': {
    title: 'Gate de liquidação',
    trailParent: 'Pagamentos',
    dockLabel: 'Gate',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/payment-settings-policy-form.quick-panel.js');
      return {
        default: module.PaymentSettingsPolicyFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'payment-method-charges-form': {
    title: 'Editar encargos',
    trailParent: 'Pagamentos',
    dockLabel: 'Encargos',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/payment-method-charges-form.quick-panel.js');
      return {
        default: module.PaymentMethodChargesFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'card-fee-installments-form': {
    title: 'Quantidade de vezes',
    trailParent: 'Pagamentos',
    dockLabel: 'Parcelas',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/card-fee-installments-form.quick-panel.js');
      return {
        default: module.CardFeeInstallmentsFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'payment-method-financial-accounts-form': {
    title: 'Contas por meio',
    trailParent: 'Pagamentos',
    dockLabel: 'Contas',
    size: 'md',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/payment-method-financial-accounts-form.quick-panel.js');
      return {
        default:
          module.PaymentMethodFinancialAccountsFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'payment-account-bank-form': {
    title: 'Dados bancários',
    trailParent: 'Pagamentos',
    dockLabel: 'Banco',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/payment-account-bank-form.quick-panel.js');
      return {
        default: module.PaymentAccountBankFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'bank-agreement-form': {
    title: 'Novo convênio',
    trailParent: 'Pagamentos',
    dockLabel: 'Convênio',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/finance/surfaces/bank-agreement-form.quick-panel.js');
      return {
        default: module.BankAgreementFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'collection-charge-emit': {
    title: 'Emitir cobrança CNAB',
    trailParent: 'Cobranças',
    dockLabel: 'Emitir',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/collection-charge-emit.quick-panel.js');
      return {
        default: module.CollectionChargeEmitQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'collection-charge-boleto': {
    title: 'Boleto',
    trailParent: 'Cobranças',
    dockLabel: 'Boleto',
    size: 'xl',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/collection-charge-boleto.quick-panel.js');
      return {
        default: module.CollectionChargeBoletoQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'collection-remittance': {
    title: 'Gerar remessa CNAB',
    trailParent: 'Cobranças',
    dockLabel: 'Remessa',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/collection-remittance.quick-panel.js');
      return {
        default: module.CollectionRemittanceQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'collection-return-upload': {
    title: 'Importar retorno CNAB',
    trailParent: 'Cobranças',
    dockLabel: 'Retorno',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/collection-return-upload.quick-panel.js');
      return {
        default: module.CollectionReturnUploadQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'payment-remittance': {
    title: 'Gerar remessa de pagamento',
    trailParent: 'Contas a pagar',
    dockLabel: 'Remessa',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/finance/surfaces/payment-remittance.quick-panel.js');
      return {
        default: module.PaymentRemittanceQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'payment-return-upload': {
    title: 'Importar retorno de pagamento',
    trailParent: 'Contas a pagar',
    dockLabel: 'Retorno',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/payment-return-upload.quick-panel.js');
      return {
        default: module.PaymentReturnUploadQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'bank-statement-import': {
    title: 'Importar OFX',
    trailParent: 'Conciliação',
    dockLabel: 'OFX',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/bank-statement-import.quick-panel.js');
      return {
        default: module.BankStatementImportQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'bank-statement-line-match': {
    title: 'Conciliar linha',
    trailParent: 'Conciliação',
    dockLabel: 'Match',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/bank-statement-line-match.quick-panel.js');
      return {
        default: module.BankStatementLineMatchQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'financial-account-form': {
    title: 'Conta financeira',
    trailParent: 'Pagamentos',
    dockLabel: 'Conta',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/financial-account-form.quick-panel.js');
      return {
        default: module.FinancialAccountFormQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'cash-session-open': {
    title: 'Abrir sessão de caixa',
    trailParent: 'Caixa',
    dockLabel: 'Sessão',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/finance/surfaces/cash-session-open.quick-panel.js');
      return {
        default: module.CashSessionOpenQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'cash-session-sangria': {
    title: 'Sangria',
    trailParent: 'Caixa',
    dockLabel: 'Sangria',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/cash-session-sangria.quick-panel.js');
      return {
        default: module.CashSessionSangriaQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'cash-session-suprimento': {
    title: 'Suprimento',
    trailParent: 'Caixa',
    dockLabel: 'Suprimento',
    size: 'lg',
    component: lazy(async () => {
      const module =
        await import('@/features/finance/surfaces/cash-session-suprimento.quick-panel.js');
      return {
        default: module.CashSessionSuprimentoQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
  'cash-session-close': {
    title: 'Fechar sessão',
    trailParent: 'Caixa',
    dockLabel: 'Fechar',
    size: 'lg',
    component: lazy(async () => {
      const module = await import('@/features/finance/surfaces/cash-session-close.quick-panel.js');
      return {
        default: module.CashSessionCloseQuickPanel as ComponentType<QuickPanelParams>,
      };
    }),
  },
};
